import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import openSocket from "../../services/socket-io";

import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Add from "@material-ui/icons/Add";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import ArrowUpward from "@material-ui/icons/ArrowUpward";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import GetApp from "@material-ui/icons/GetApp";
import PlaylistAddCheck from "@material-ui/icons/PlaylistAddCheck";
import Remove from "@material-ui/icons/Remove";
import Pause from "@material-ui/icons/Pause";
import PlayArrow from "@material-ui/icons/PlayArrow";
import SearchIcon from "@material-ui/icons/Search";
import Stop from "@material-ui/icons/Stop";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import MessageBubblePreview from "../../components/MessageBubblePreview";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  buildWhatsappTabs,
  getWhatsappTabParams,
  orphanTabPrefix,
} from "../../utils/connectionsTabs";
import {
  buildMessageVariables,
  fetchContactCustomFieldNames,
  fetchWasheetMessageVariables,
} from "../../utils/messageVariables";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

const acceptedMedia =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp";
const NO_SEQUENCE = "no-sequence";
const NO_CAMPAIGN_FUNNEL = "__no_campaign_funnel__";
const NO_CAMPAIGN_STAGE = "__no_campaign_stage__";
const NO_CAMPAIGN_LIST = "__no_campaign_list__";

const createBlock = () => ({
  text: "",
  delaySeconds: 0,
  mentionAll: false,
  file: null,
  media: null,
});

const initialForm = {
  name: "",
  audienceType: "manual_selection",
  labelIds: [],
  contactLabelField: "",
  contactLabelNames: [],
  funnelId: "",
  stageId: "",
  campaignListId: "",
  actionType: "message",
  chatbotSequenceId: "",
  minDelaySeconds: 20,
  maxDelaySeconds: 90,
  scheduleMode: "immediate",
  scheduledAt: "",
  saveSelectionAsList: false,
  listName: "",
};

const statusLabels = {
  draft: "Borrador",
  running: "En ejecucion",
  scheduled: "Programada",
  paused: "Pausada",
  completed: "Completada",
  failed: "Fallida",
  cancelled: "Cancelada",
};

const recipientStatusLabels = {
  pending: "Pendiente",
  processing: "Enviando",
  sent: "Enviado",
  failed: "Fallido",
  skipped: "Omitido",
};

const audienceLabels = {
  manual_selection: "Selección manual",
  people: "Personas",
  groups: "Grupos/Comunidades",
  labels: "Etiquetas",
  contact_labels: "Etiquetas de contactos",
  funnel_stage: "Embudo y etapa",
  saved_list: "Lista guardada",
};

const formatSequenceOption = sequence =>
  `ID: ${sequence.id} - ${sequence.name}`;

const formatListOption = list =>
  list ? `ID: ${list.id} - ${list.name}` : "";

const getDatePartsInTimeZone = (value, timeZone) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const values = parts.reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour === "24" ? "00" : values.hour,
    minute: values.minute,
    second: values.second,
  };
};

const toDateTimeLocalValue = (value, timeZone) => {
  const parts = getDatePartsInTimeZone(value, timeZone);
  if (!parts) return "";
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const campaignStartWarning =
  "Asegúrese de comprender el propósito para el que está utilizando nuestra plataforma. No está destinado a ser utilizado de ninguna manera que constituya un uso indebido o spam. Úselo de manera responsable para comunicarse con sus contactos conocidos, familiares, amigos o clientes. Tenga cuidado al usar nuestra plataforma.\n\n" +
  "Su comportamiento inapropiado continuo podría llevar a que su cuenta sea bloqueada permanentemente.\n\n" +
  "Los envíos masivos a contactos con los que nunca has interactuado pueden ser causa de bloqueo al ser considerados SPAM.\n\n" +
  "La plataforma no se hace responsable por los casos de bloqueos y no se emitiran reembolsos en estos casos.\n\n" +
  "Debes asegurarte de seguir las políticas y normas de uso correspondientes.";

const reducer = (state, action) => {
  if (action.type === "LOAD") return action.payload;

  if (action.type === "UPDATE") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex(item => item.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    }

    return [campaign, ...state];
  }

  if (action.type === "DELETE") {
    return state.filter(campaign => Number(campaign.id) !== Number(action.payload));
  }

  if (action.type === "RESET") return [];

  return state;
};

const campaignOwnsConnectionTab = (campaign, selectedConnectionId) => {
  if (!selectedConnectionId) return true;
  const selectedValue = String(selectedConnectionId);

  if (selectedValue.startsWith(orphanTabPrefix)) {
    return String(campaign?.orphanedConnectionId || "") ===
      selectedValue.replace(orphanTabPrefix, "");
  }

  return String(campaign?.connectionId || "") === selectedValue;
};

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& .MuiTextField-root": { width: "100%" },
      "& .MuiButton-root": { minHeight: 42 },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: { display: "none" },
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "start",
  },
  mobileCardGrid: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  mobileValue: {
    textAlign: "right",
    wordBreak: "break-word",
  },
  actionButtons: {
    display: "inline-flex",
    gap: theme.spacing(0.5),
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "140px 140px minmax(180px, 1fr) minmax(240px, 1.2fr)",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  formGridFull: {
    gridColumn: "1 / -1",
  },
  section: {
    marginTop: theme.spacing(2),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  selectorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  listActions: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  selectWithActions: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto auto",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  listEditBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  ticketGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gridAutoRows: "minmax(64px, auto)",
    gap: theme.spacing(1),
    maxHeight: "calc((64px * 6) + (8px * 5))",
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    ...theme.scrollbarStyles,
  },
  ticketGridWrapper: {
    position: "relative",
    minHeight: 74,
  },
  previewLoadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    padding: theme.spacing(0.75),
    pointerEvents: "none",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0))",
  },
  emptyPreview: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 74,
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
  },
  ticketCard: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  ticketName: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  ticketNumber: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  block: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.default,
  },
  blockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  blockActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  blockGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 165px",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageBlockGrid: {
    display: "grid",
    gridTemplateColumns: "330px minmax(0, 1fr) 90px",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "290px minmax(0, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageDelayField: {
    [theme.breakpoints.down("sm")]: {
      gridColumn: "2",
    },
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  fileName: {
    flex: 1,
    minWidth: 0,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  helper: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  variablesBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },
  variablesLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  variablesChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  progressWrap: {
    minWidth: 150,
    display: "grid",
    gap: theme.spacing(0.5),
    justifyItems: "center",
  },
  progressText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  progressSummary: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  progressMetric: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    textAlign: "center",
  },
  progressToolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  progressTableCard: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  startWarningText: {
    whiteSpace: "pre-line",
    color: theme.palette.text.secondary,
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
}));

const Campaigns = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const previewRequestRef = useRef(0);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [labels, setLabels] = useState([]);
  const [contactLabelFilters, setContactLabelFilters] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [lists, setLists] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [blocks, setBlocks] = useState([createBlock()]);
  const [previewTickets, setPreviewTickets] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSearchParam, setPreviewSearchParam] = useState("");
  const [previewPageNumber, setPreviewPageNumber] = useState(1);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(20);
  const [previewTicketCount, setPreviewTicketCount] = useState(0);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [startConfirmation, setStartConfirmation] = useState(null);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [washeetVariables, setWasheetVariables] = useState([]);
  const [startTermsAccepted, setStartTermsAccepted] = useState(false);
  const [progressCampaign, setProgressCampaign] = useState(null);
  const [progressRecipients, setProgressRecipients] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [listEditMode, setListEditMode] = useState(false);
  const [originalListTicketIds, setOriginalListTicketIds] = useState([]);
  const [listUpdateConfirmation, setListUpdateConfirmation] = useState(false);
  const [deletingList, setDeletingList] = useState(null);
  const [renamingList, setRenamingList] = useState(null);
  const [renamingListName, setRenamingListName] = useState("");
  const [now, setNow] = useState(Date.now());
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );
  const selectedFunnel = funnels.find(
    funnel => String(funnel.id) === String(form.funnelId)
  );
  const runningCampaign = campaigns.find(campaign => campaign.status === "running");
  const immediateCampaigns = useMemo(
    () => campaigns.filter(campaign => !campaign.scheduledAt),
    [campaigns]
  );
  const scheduledCampaigns = useMemo(
    () => campaigns.filter(campaign => Boolean(campaign.scheduledAt)),
    [campaigns]
  );
  const selectedList = lists.find(list => String(list.id) === String(form.campaignListId));
  const selectedContactLabelFilter = contactLabelFilters.find(
    field => String(field.name) === String(form.contactLabelField)
  );
  const filteredPreviewTickets = useMemo(() => {
    return previewTickets;
  }, [previewTickets]);
  const sortedSelectedListIds = useMemo(
    () => selectedTicketIds.map(String).sort(),
    [selectedTicketIds]
  );
  const sortedOriginalListIds = useMemo(
    () => originalListTicketIds.map(String).sort(),
    [originalListTicketIds]
  );
  const listSelectionChanged =
    listEditMode &&
    (sortedSelectedListIds.length !== sortedOriginalListIds.length ||
      sortedSelectedListIds.some((ticketId, index) => ticketId !== sortedOriginalListIds[index]));
  const getTicketLabel = ticketId => {
    const ticket = previewTickets.find(item => String(item.id) === String(ticketId));
    const contact = ticket?.contact || {};
    if (privacyCensorSettings.contactNames) return "Contacto privado";
    return `${contact.name || "Sin nombre"} - ${contact.number || "-"}`;
  };
  const listAddedTicketIds = listSelectionChanged
    ? sortedSelectedListIds.filter(ticketId => !sortedOriginalListIds.includes(ticketId))
    : [];
  const listRemovedTicketIds = listSelectionChanged
    ? sortedOriginalListIds.filter(ticketId => !sortedSelectedListIds.includes(ticketId))
    : [];
  const messageVariables = useMemo(
    () => buildMessageVariables(customFieldNames, washeetVariables),
    [customFieldNames, washeetVariables]
  );
  const selectedHasGroup = useMemo(() => {
    const selectedIds = new Set(selectedTicketIds.map(String));

    return previewTickets.some(ticket => {
      if (!selectedIds.has(String(ticket.id))) return false;
      return Boolean(ticket.isGroup || ticket.contact?.isGroup);
    });
  }, [previewTickets, selectedTicketIds]);

  const campaignMatchesSearch = campaign => {
    const search = String(searchParam || "").trim().toLowerCase();
    if (!search) return true;

    return [
      campaign?.name,
      campaign?.status,
      campaign?.audienceType,
    ].some(value => String(value || "").toLowerCase().includes(search));
  };

  useEffect(() => {
    Promise.all([
      fetchContactCustomFieldNames(),
      fetchWasheetMessageVariables(),
      api.get("/contacts/label-filters").catch(() => ({ data: { fields: [] } })),
    ])
      .then(([fields, variables, labelFilters]) => {
        setCustomFieldNames(fields);
        setWasheetVariables(variables);
        setContactLabelFilters(labelFilters.data?.fields || []);
      })
      .catch(() => undefined);

    const fetchConnections = async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups"),
        ]);
        const tabs = buildWhatsappTabs(data || [], orphanData || []);
        setConnections(data || []);
        setOrphanGroups(orphanData || []);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchConnections();
  }, []);

  useEffect(() => {
    if (!selectedConnectionId) return;

    const fetchSupportData = async () => {
      try {
        const tabParams = getWhatsappTabParams(selectedConnectionId);
        const [
          { data: labelsData },
          { data: funnelsData },
          { data: sequencesData },
          { data: listsData },
        ] = await Promise.all([
          api.get("/labels", { params: tabParams }),
          api.get("/funnels"),
          api.get("/chatbot/sequences", {
            params: { all: true, pageNumber: 1, searchParam: "", ...tabParams },
          }),
          api.get("/campaigns/lists", { params: tabParams }),
        ]);

        setLabels(labelsData || []);
        setFunnels(funnelsData.funnels || []);
        setSequences(sequencesData.sequences || []);
        setLists(listsData.lists || []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchSupportData();
  }, [selectedConnectionId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
  }, [searchParam, selectedConnectionId]);

  useEffect(() => {
    if (!selectedConnectionId) return;
    setLoading(true);

    const fetchCampaigns = async () => {
      try {
        const { data } = await api.get("/campaigns", {
          params: {
            searchParam,
            ...getWhatsappTabParams(selectedConnectionId),
          },
        });
        dispatch({ type: "LOAD", payload: data.campaigns || [] });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [searchParam, selectedConnectionId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("campaigns", data => {
      if (data.action === "create" || data.action === "update") {
        if (
          campaignOwnsConnectionTab(data.campaign, selectedConnectionId) &&
          campaignMatchesSearch(data.campaign)
        ) {
          dispatch({ type: "UPDATE", payload: data.campaign });
        } else if (data.action === "update") {
          dispatch({ type: "DELETE", payload: data.campaign.id });
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: data.campaignId });
      }
    });

    return () => socket.disconnect();
  }, [selectedConnectionId, searchParam]);

  useEffect(() => {
    if (!modalOpen || !selectedConnectionId) return;

    const timeout = setTimeout(() => {
      fetchAudiencePreview();
    }, 350);

    return () => clearTimeout(timeout);
  }, [
    modalOpen,
    selectedConnectionId,
    form.audienceType,
    form.labelIds,
    form.contactLabelField,
    form.contactLabelNames,
    form.funnelId,
    form.stageId,
    form.campaignListId,
    previewSearchParam,
    previewPageNumber,
    previewRowsPerPage,
  ]);

  useEffect(() => {
    setPreviewPageNumber(1);
  }, [
    previewSearchParam,
    selectedConnectionId,
    form.audienceType,
    form.labelIds,
    form.contactLabelField,
    form.contactLabelNames,
    form.funnelId,
    form.stageId,
    form.campaignListId,
  ]);

  const resetModalState = () => {
    setEditingCampaign(null);
    setForm({
      ...initialForm,
      name: "",
      listName: "",
    });
    setBlocks([createBlock()]);
    setPreviewTickets([]);
    setPreviewSearchParam("");
    setPreviewPageNumber(1);
    setPreviewRowsPerPage(20);
    setPreviewTicketCount(0);
    setSelectedTicketIds([]);
    setListEditMode(false);
    setOriginalListTicketIds([]);
    setListUpdateConfirmation(false);
    setDeletingList(null);
    setRenamingList(null);
    setRenamingListName("");
  };

  const openNewCampaign = () => {
    resetModalState();
    setModalOpen(true);
  };

  const openEditCampaign = async campaign => {
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}`);
      const campaignData = data.campaign;
      setEditingCampaign(campaignData);
      setForm({
        name: campaignData.name || "",
        audienceType: campaignData.audienceType || "manual_selection",
        labelIds: (campaignData.labelIds || []).map(Number),
        contactLabelField: campaignData.contactLabelField || "",
        contactLabelNames: campaignData.contactLabelNames || [],
        funnelId: campaignData.funnelId || "",
        stageId: campaignData.stageId || "",
        campaignListId: campaignData.campaignListId || "",
        actionType: campaignData.actionType || "message",
        chatbotSequenceId: campaignData.chatbotSequenceId || "",
        minDelaySeconds: Number(campaignData.minDelaySeconds || 20),
        maxDelaySeconds: Number(campaignData.maxDelaySeconds || 90),
        scheduleMode: campaignData.status === "scheduled" ? "scheduled" : "immediate",
        scheduledAt: toDateTimeLocalValue(
          campaignData.scheduledAt,
          campaignData.connection?.timeZone
        ),
        saveSelectionAsList: false,
        listName: "",
      });
      setBlocks(
        campaignData.blocks?.length
          ? campaignData.blocks.map(block => ({
              text: block.text || "",
              delaySeconds: Number(block.delaySeconds || 0),
              mentionAll: Boolean(block.mentionAll),
              file: null,
              media: block.media || null,
            }))
          : [createBlock()]
      );
      setSelectedTicketIds(
        (data.recipients || []).map(recipient => String(recipient.ticketId))
      );
      setPreviewTickets(
        (data.recipients || [])
          .map(recipient => recipient.ticket)
          .filter(Boolean)
      );
      setModalOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetModalState();
  };

  const upsertSequence = sequence => {
    if (!sequence?.id) return;
    setSequences(prevSequences => {
      const exists = prevSequences.some(item => String(item.id) === String(sequence.id));
      return exists
        ? prevSequences.map(item => (String(item.id) === String(sequence.id) ? sequence : item))
        : [...prevSequences, sequence];
    });
  };

  const openCreateSequence = () => {
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = sequenceId => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    upsertSequence(savedSequence);
    if (savedSequenceId) {
      updateForm({ chatbotSequenceId: String(savedSequenceId) });
    }
    setSequenceModalOpen(false);
    setSequenceModalId(null);
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
  };

  const refreshCampaignLists = async () => {
    const { data } = await api.get("/campaigns/lists", {
      params: getWhatsappTabParams(selectedConnectionId),
    });
    setLists(data.lists || []);
  };

  const saveCampaignList = async () => {
    if (!form.listName.trim()) {
      toast.error("Escribe el nombre de la lista.");
      return;
    }
    if (!selectedTicketIds.length) {
      toast.error("Selecciona al menos un ticket.");
      return;
    }

    try {
      const { data } = await api.post("/campaigns/lists", {
        name: form.listName.trim(),
        connectionId: selectedConnectionId,
        ticketIds: selectedTicketIds.map(Number).filter(Boolean),
      });
      await refreshCampaignLists();
      updateForm({ listName: "", campaignListId: data.id });
      toast.success("Lista guardada correctamente.");
    } catch (err) {
      toastError(err);
    }
  };

  const renameCampaignList = async () => {
    const listId = renamingList?.id || form.campaignListId;
    const nextName = renamingListName.trim() || form.listName.trim();

    if (!listId) {
      toast.error("Selecciona una lista.");
      return;
    }
    if (!nextName) {
      toast.error("Escribe el nombre de la lista.");
      return;
    }

    try {
      await api.put(`/campaigns/lists/${listId}`, {
        name: nextName,
      });
      await refreshCampaignLists();
      updateForm({ listName: nextName });
      setRenamingList(null);
      setRenamingListName("");
      toast.success("Nombre de lista actualizado.");
    } catch (err) {
      toastError(err);
    }
  };

  const openRenameListModal = () => {
    if (!selectedList) return;
    setRenamingList(selectedList);
    setRenamingListName(selectedList.name || "");
  };

  const updateCampaignListTickets = async () => {
    if (!form.campaignListId) return;
    if (!selectedTicketIds.length) {
      toast.error("La lista debe tener al menos un ticket.");
      return;
    }

    try {
      await api.put(`/campaigns/lists/${form.campaignListId}`, {
        ticketIds: selectedTicketIds.map(Number).filter(Boolean),
      });
      await refreshCampaignLists();
      const updatedListTicketIds = selectedTicketIds.map(String);
      setOriginalListTicketIds(updatedListTicketIds);
      restoreListPreview(updatedListTicketIds);
      setListUpdateConfirmation(false);
      setListEditMode(false);
      toast.success("Tickets de la lista actualizados.");
    } catch (err) {
      toastError(err);
    }
  };

  const deleteCampaignList = async () => {
    const listId = deletingList?.id || form.campaignListId;
    if (!listId) {
      toast.error("Selecciona una lista.");
      return;
    }

    try {
      await api.delete(`/campaigns/lists/${listId}`);
      await refreshCampaignLists();
      updateForm({ campaignListId: "", listName: "" });
      setDeletingList(null);
      setListEditMode(false);
      setOriginalListTicketIds([]);
      toast.success("Lista eliminada correctamente.");
    } catch (err) {
      toastError(err);
    }
  };

  const fetchCampaignProgress = async (campaign, options = {}) => {
    if (!campaign?.id) return;
    if (!options.silent) setProgressLoading(true);

    try {
      const { data } = await api.get(`/campaigns/${campaign.id}`);
      setProgressCampaign(data.campaign);
      setProgressRecipients(data.recipients || []);
    } catch (err) {
      toastError(err);
    } finally {
      if (!options.silent) setProgressLoading(false);
    }
  };

  const openProgressModal = campaign => {
    setProgressCampaign(campaign);
    setProgressRecipients([]);
    fetchCampaignProgress(campaign);
  };

  const closeProgressModal = () => {
    setProgressCampaign(null);
    setProgressRecipients([]);
  };

  useEffect(() => {
    if (!progressCampaign) return undefined;

    const interval = setInterval(() => {
      setNow(Date.now());
      fetchCampaignProgress(progressCampaign, { silent: true });
    }, 1000);

    return () => clearInterval(interval);
  }, [progressCampaign?.id]);

  useEffect(() => {
    if (selectedHasGroup) return;

    setBlocks(prevBlocks => {
      if (!prevBlocks.some(block => block.mentionAll)) return prevBlocks;

      return prevBlocks.map(block => ({
        ...block,
        mentionAll: false,
      }));
    });
  }, [selectedHasGroup]);

  const updateForm = patch => {
    setForm(prevState => ({ ...prevState, ...patch }));
  };

  const updateBlock = (index, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...patch } : block
      )
    );
  };

  const moveBlock = (index, direction) => {
    setBlocks(prevBlocks => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prevBlocks.length) return prevBlocks;
      const nextBlocks = [...prevBlocks];
      const current = nextBlocks[index];
      nextBlocks[index] = nextBlocks[nextIndex];
      nextBlocks[nextIndex] = current;
      return nextBlocks;
    });
  };

  const removeBlock = index => {
    setBlocks(prevBlocks =>
      prevBlocks.length === 1
        ? [createBlock()]
        : prevBlocks.filter((_, blockIndex) => blockIndex !== index)
    );
  };

  const addVariableToBlock = (index, variable) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) => {
        if (blockIndex !== index) return block;
        const separator = block.text?.trim() ? " " : "";
        return { ...block, text: `${block.text || ""}${separator}${variable}` };
      })
    );
  };

  const fetchAudiencePreview = async () => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) return;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setPreviewLoading(true);

    try {
      const params = {
        ...getWhatsappTabParams(selectedConnectionId),
        audienceType: form.audienceType,
        labelIds: JSON.stringify(form.labelIds || []),
        contactLabelField: form.contactLabelField || "",
        contactLabelNames: JSON.stringify(form.contactLabelNames || []),
        funnelId: form.funnelId || "",
        stageId: form.stageId || "",
        campaignListId: form.campaignListId || "",
        ticketIds: JSON.stringify(
          form.audienceType === "manual_selection"
            ? []
            : selectedTicketIds.map(Number).filter(Boolean)
        ),
        searchParam: previewSearchParam,
        pageNumber: previewPageNumber,
        rowsPerPage: previewRowsPerPage,
      };
      const { data } = await api.get("/campaigns/audience-preview", { params });
      if (requestId !== previewRequestRef.current) return;
      const tickets = data.tickets || [];
      setPreviewTickets(tickets);
      setPreviewTicketCount(data.count || tickets.length);

      if (form.audienceType !== "manual_selection") {
        setSelectedTicketIds(tickets.map(ticket => String(ticket.id)));
        if (form.audienceType === "saved_list") {
          setOriginalListTicketIds(tickets.map(ticket => String(ticket.id)));
        }
      }
    } catch (err) {
      if (requestId !== previewRequestRef.current) return;
      if (
        ![
          "ERR_CAMPAIGN_LABEL_REQUIRED",
          "ERR_CAMPAIGN_CONTACT_LABEL_REQUIRED",
          "ERR_CAMPAIGN_STAGE_REQUIRED",
          "ERR_CAMPAIGN_LIST_REQUIRED",
        ].includes(err?.response?.data?.error)
      ) {
        toastError(err);
      }
    } finally {
      if (requestId === previewRequestRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  const toggleTicket = ticketId => {
    const normalizedId = String(ticketId);
    setSelectedTicketIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllPreviewTickets = () => {
    const previewIds = filteredPreviewTickets.map(ticket => String(ticket.id));
    const allSelected =
      previewIds.length > 0 &&
      previewIds.every(ticketId => selectedTicketIds.includes(ticketId));

    if (allSelected) {
      setSelectedTicketIds(prevState =>
        prevState.filter(ticketId => !previewIds.includes(ticketId))
      );
      return;
    }

    setSelectedTicketIds(prevState =>
      Array.from(new Set([...prevState, ...previewIds]))
    );
  };

  const handlePreviewPageChange = (_event, nextPage) => {
    setPreviewPageNumber(nextPage + 1);
  };

  const handlePreviewRowsPerPageChange = event => {
    setPreviewRowsPerPage(Number(event.target.value));
    setPreviewPageNumber(1);
  };

  const loadManualTicketsForListEdit = async () => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) return;

    const nextSearchParam = "";
    const nextPageNumber = 1;
    setPreviewSearchParam(nextSearchParam);
    setPreviewPageNumber(nextPageNumber);
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/campaigns/audience-preview", {
        params: {
          ...getWhatsappTabParams(selectedConnectionId),
          audienceType: "manual_selection",
          ticketIds: JSON.stringify([]),
          searchParam: nextSearchParam,
          pageNumber: nextPageNumber,
          rowsPerPage: previewRowsPerPage,
        },
      });
      const tickets = data.tickets || [];
      setPreviewTickets(tickets);
      setPreviewTicketCount(data.count || tickets.length);
    } catch (err) {
      toastError(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const restoreListPreview = ticketIds => {
    const normalizedIds = ticketIds.map(String);
    const idSet = new Set(normalizedIds);

    setSelectedTicketIds(normalizedIds);
    setPreviewSearchParam("");
    setPreviewTickets(prevTickets =>
      prevTickets.filter(ticket => idSet.has(String(ticket.id)))
    );
  };

  const toggleListEditMode = async () => {
    if (!form.campaignListId) return;

    if (listEditMode) {
      restoreListPreview(originalListTicketIds);
      setListEditMode(false);
      return;
    }

    setOriginalListTicketIds(selectedTicketIds.map(String));
    setListEditMode(true);
    setPreviewSearchParam("");
    await loadManualTicketsForListEdit();
  };

  const buildFormData = status => {
    const normalizedBlocks = blocks.map(block => ({
      text: block.text || "",
      delaySeconds: Number(block.delaySeconds || 0),
      mentionAll: selectedHasGroup && Boolean(block.mentionAll),
      media: block.file ? undefined : block.media,
    }));
    const formData = new FormData();
    const scheduledAt = status === "scheduled" ? form.scheduledAt : "";

    Object.entries({
      ...form,
      status,
      scheduledAt,
      connectionId: selectedConnectionId,
      labelIds: JSON.stringify(form.labelIds || []),
      contactLabelField: form.contactLabelField || "",
      contactLabelNames: JSON.stringify(form.contactLabelNames || []),
      ticketIds: JSON.stringify(selectedTicketIds.map(Number).filter(Boolean)),
      blocks: JSON.stringify(normalizedBlocks),
    }).forEach(([key, value]) => {
      formData.append(key, value === undefined || value === null ? "" : value);
    });

    blocks.forEach((block, index) => {
      if (block.file) formData.append(`blockFile_${index}`, block.file);
    });

    return formData;
  };

  const validateBeforeSave = () => {
    if (!form.name.trim()) {
      toast.error("Escribe el nombre de la campaña.");
      return false;
    }
    if (!selectedTicketIds.length) {
      toast.error("Selecciona al menos un ticket.");
      return false;
    }
    if (
      form.audienceType === "contact_labels" &&
      (!form.contactLabelField || !form.contactLabelNames.length)
    ) {
      toast.error("Selecciona el campo y al menos una etiqueta de contacto.");
      return false;
    }
    if (form.actionType === "sequence" && !form.chatbotSequenceId) {
      toast.error("Selecciona una secuencia.");
      return false;
    }
    if (
      form.actionType === "message" &&
      !blocks.some(block => block.text.trim() || block.file || block.media)
    ) {
      toast.error("Agrega al menos un mensaje o archivo.");
      return false;
    }
    if (form.scheduleMode === "scheduled") {
      const scheduledTime = new Date(form.scheduledAt);
      if (!form.scheduledAt || Number.isNaN(scheduledTime.getTime())) {
        toast.error("Selecciona la fecha y hora de ejecucion.");
        return false;
      }
    }
    return true;
  };

  const saveCampaign = async status => {
    if (!validateBeforeSave()) return;
    setSaving(true);

    try {
      const formData = buildFormData(status);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const { data } = editingCampaign
        ? await api.put(`/campaigns/${editingCampaign.id}`, formData, config)
        : await api.post("/campaigns", formData, config);

      dispatch({ type: "UPDATE", payload: data });
      if (progressCampaign && Number(progressCampaign.id) === Number(data.id)) {
        setProgressCampaign(data);
        fetchCampaignProgress(data, { silent: true });
      }
      toast.success("Campaña guardada correctamente.");
      closeModal();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const requestSaveAndStart = () => {
    if (!validateBeforeSave()) return;
    if (form.scheduleMode === "scheduled") {
      saveCampaign("scheduled");
      return;
    }
    if (runningCampaign && (!editingCampaign || runningCampaign.id !== editingCampaign.id)) {
      toast.error("Ya hay una campaña en progreso.");
      return;
    }
    setStartTermsAccepted(false);
    setStartConfirmation({ type: "save" });
  };

  const requestStartCampaign = campaign => {
    if (runningCampaign && runningCampaign.id !== campaign.id) {
      toast.error("Ya hay una campaña en progreso.");
      return;
    }
    setStartTermsAccepted(false);
    setStartConfirmation({ type: "campaign", campaign });
  };

  const closeStartConfirmation = () => {
    if (saving) return;
    setStartConfirmation(null);
    setStartTermsAccepted(false);
  };

  const confirmStartCampaign = async () => {
    const confirmation = startConfirmation;
    setStartConfirmation(null);
    setStartTermsAccepted(false);

    if (confirmation?.type === "save") {
      await saveCampaign("running");
      return;
    }

    if (confirmation?.campaign) {
      await updateCampaignStatus(confirmation.campaign, "start");
    }
  };

  const updateCampaignStatus = async (campaign, action) => {
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/${action}`);
      dispatch({ type: "UPDATE", payload: data });
      if (progressCampaign && Number(progressCampaign.id) === Number(data.id)) {
        setProgressCampaign(data);
        fetchCampaignProgress(data, { silent: true });
      }
      toast.success("Campaña actualizada.");
    } catch (err) {
      toastError(err);
    }
  };

  const deleteCampaign = async () => {
    try {
      await api.delete(`/campaigns/${deletingCampaign.id}`);
      dispatch({ type: "DELETE", payload: deletingCampaign.id });
      toast.success("Campaña eliminada correctamente.");
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingCampaign(null);
    }
  };

  const renderStatus = status => (
    <Chip size="small" label={statusLabels[status] || status} />
  );

  const renderProgress = campaign => (
    <div className={classes.progressWrap}>
      <Typography variant="body2">
        {campaign.sentCount || 0}/{campaign.totalRecipients || 0}
      </Typography>
      <Typography className={classes.progressText}>
        {campaign.pendingCount || 0} pendientes · {campaign.failedCount || 0} fallidos
      </Typography>
      <Button size="small" color="primary" onClick={() => openProgressModal(campaign)}>
        Ver progreso
      </Button>
    </div>
  );

  const formatDateTime = (value, timeZone) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("es-CR", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getRemainingTime = value => {
    if (!value) return "-";
    const seconds = Math.max(0, Math.ceil((new Date(value).getTime() - now) / 1000));
    if (seconds <= 0) return "Listo para enviar";
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return minutes ? `${minutes}m ${rest}s` : `${rest}s`;
  };

  const downloadProgressReport = () => {
    if (!progressCampaign) return;

    const pending = progressRecipients.filter(recipient =>
      ["pending", "processing"].includes(recipient.status)
    );
    const sent = progressRecipients.filter(recipient => recipient.status === "sent");
    const contactLine = recipient => {
      const contact = recipient.contact || recipient.ticket?.contact || {};
      return `${contact.name || "Sin nombre"} - ${contact.number || "-"}${
        recipient.sentAt ? ` - Enviado: ${formatDateTime(recipient.sentAt)}` : ""
      }`;
    };

    const lines = [
      `Reporte de campaña: ${progressCampaign.name}`,
      `Generado: ${formatDateTime(new Date())}`,
      "",
      "PENDIENTES",
      ...(pending.length ? pending.map(contactLine) : ["Sin pendientes"]),
      "",
      "ENVIADOS",
      ...(sent.length ? sent.map(contactLine) : ["Sin enviados"]),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `campana-${progressCampaign.id}-reporte.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const renderProgressRecipient = recipient => {
    const contact = recipient.contact || recipient.ticket?.contact || {};
    const remainingTime =
      recipient.status === "processing"
        ? "Enviando"
        : recipient.status === "pending" && progressCampaign?.status === "paused"
        ? "Pausada"
        : recipient.status === "pending" && progressCampaign?.status === "cancelled"
        ? "Detenida"
        : recipient.status !== "pending"
        ? "-"
        : getRemainingTime(recipient.nextRunAt);

    return (
      <TableRow key={recipient.id}>
        <TableCell>
          <div className={classes.progressTableCard}>
            <Avatar
              src={privacyCensorSettings.contactPhotos ? undefined : contact.profilePicUrl}
              className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
            >
              {privacyCensorSettings.contactNames ? undefined : contact.name?.[0]}
            </Avatar>
            <div>
              <Typography
                className={
                  privacyCensorSettings.contactNames
                    ? classes.censoredText
                    : classes.ticketName
                }
              >
                {privacyCensorSettings.contactNames
                  ? "Contacto privado"
                  : contact.name || "Sin nombre"}
              </Typography>
              <Typography
                className={
                  privacyCensorSettings.contactNames
                    ? classes.censoredText
                    : classes.ticketNumber
                }
              >
                {privacyCensorSettings.contactNames
                  ? "Telefono privado"
                  : contact.number || "-"}
              </Typography>
            </div>
          </div>
        </TableCell>
        <TableCell align="center">
          {recipientStatusLabels[recipient.status] || recipient.status}
        </TableCell>
        <TableCell align="center">{formatDateTime(recipient.sentAt)}</TableCell>
        <TableCell align="center">{remainingTime}</TableCell>
      </TableRow>
    );
  };

  const renderActions = campaign => (
    <div className={classes.actionButtons}>
      {["draft", "scheduled", "paused", "cancelled", "completed", "failed"].includes(campaign.status) && (
        <Tooltip title="Iniciar">
          <span>
            <IconButton
              size="small"
              disabled={Boolean(runningCampaign && runningCampaign.id !== campaign.id)}
              onClick={() => requestStartCampaign(campaign)}
            >
              <PlayArrow fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {campaign.status === "running" && (
        <Tooltip title="Pausar">
          <IconButton size="small" onClick={() => updateCampaignStatus(campaign, "pause")}>
            <Pause fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {["running", "scheduled", "paused"].includes(campaign.status) && (
        <Tooltip title="Cancelar">
          <IconButton size="small" onClick={() => updateCampaignStatus(campaign, "cancel")}>
            <Stop fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Editar">
        <span>
          <IconButton
            size="small"
            disabled={campaign.status === "running"}
            onClick={() => openEditCampaign(campaign)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" onClick={() => setDeletingCampaign(campaign)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderCampaignRow = campaign => (
    <TableRow key={campaign.id}>
      <TableCell>{campaign.name}</TableCell>
      <TableCell align="center">{campaign.connection?.name || "-"}</TableCell>
      <TableCell align="center">
        {audienceLabels[campaign.audienceType] || campaign.audienceType}
      </TableCell>
      <TableCell align="center">{renderStatus(campaign.status)}</TableCell>
      <TableCell align="center">{renderProgress(campaign)}</TableCell>
      <TableCell align="center">
        {campaign.actionType === "sequence"
          ? `Secuencia ${campaign.chatbotSequence?.name || ""}`
          : `${campaign.blocks?.length || 0} bloque(s)`}
      </TableCell>
      <TableCell align="center">{renderActions(campaign)}</TableCell>
    </TableRow>
  );

  const renderScheduledCampaignRow = campaign => (
    <TableRow key={campaign.id}>
      <TableCell>{campaign.name}</TableCell>
      <TableCell align="center">{campaign.connection?.name || "-"}</TableCell>
      <TableCell align="center">
        {audienceLabels[campaign.audienceType] || campaign.audienceType}
      </TableCell>
      <TableCell align="center">
        {formatDateTime(campaign.scheduledAt, campaign.connection?.timeZone)}
      </TableCell>
      <TableCell align="center">{renderProgress(campaign)}</TableCell>
      <TableCell align="center">
        {campaign.actionType === "sequence"
          ? `Secuencia ${campaign.chatbotSequence?.name || ""}`
          : `${campaign.blocks?.length || 0} bloque(s)`}
      </TableCell>
      <TableCell align="center">{renderActions(campaign)}</TableCell>
    </TableRow>
  );

  const renderMobileCampaign = campaign => (
    <div className={classes.mobileCard} key={campaign.id}>
      <div className={classes.mobileCardHeader}>
        <div>
          <Typography variant="subtitle2">{campaign.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {campaign.connection?.name || "-"}
          </Typography>
        </div>
        {renderActions(campaign)}
      </div>
      <Divider />
      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Audiencia</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {audienceLabels[campaign.audienceType] || campaign.audienceType}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Estado</span>
          <span>{renderStatus(campaign.status)}</span>
        </div>
        {campaign.status === "scheduled" && (
          <div className={classes.mobileField}>
            <span className={classes.mobileLabel}>Programada</span>
            <Typography variant="body2" className={classes.mobileValue}>
              {formatDateTime(campaign.scheduledAt, campaign.connection?.timeZone)}
            </Typography>
          </div>
        )}
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Progreso</span>
          <div className={classes.mobileValue}>{renderProgress(campaign)}</div>
        </div>
      </div>
    </div>
  );

  const renderTicketCard = ticket => (
    <div className={classes.ticketCard} key={ticket.id}>
      <Avatar
        src={
          privacyCensorSettings.contactPhotos
            ? undefined
            : ticket.contact?.profilePicUrl
        }
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
      >
        {privacyCensorSettings.contactNames ? undefined : ticket.contact?.name?.[0]}
      </Avatar>
      <div>
        <Typography
          className={
            privacyCensorSettings.contactNames
              ? classes.censoredText
              : classes.ticketName
          }
        >
          {privacyCensorSettings.contactNames
            ? "Contacto privado"
            : ticket.contact?.name || "Sin nombre"}
        </Typography>
        <Typography
          className={
            privacyCensorSettings.contactNames
              ? classes.censoredText
              : classes.ticketNumber
          }
        >
          {privacyCensorSettings.contactNames
            ? "Telefono privado"
            : ticket.contact?.number || "-"}
        </Typography>
      </div>
      <Checkbox
        color="primary"
        checked={selectedTicketIds.includes(String(ticket.id))}
        onChange={() => toggleTicket(ticket.id)}
      />
    </div>
  );

  const renderAudienceControls = () => (
    <>
      <div className={classes.selectorGrid}>
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>Audiencia</InputLabel>
          <Select
            value={form.audienceType}
            onChange={event => {
              setListEditMode(false);
              setOriginalListTicketIds([]);
              setPreviewSearchParam("");
              setPreviewTickets([]);
              setSelectedTicketIds([]);
              updateForm({
                audienceType: event.target.value,
                labelIds: [],
                contactLabelField: "",
                contactLabelNames: [],
                funnelId: "",
                stageId: "",
                campaignListId: "",
                listName: "",
                saveSelectionAsList: false,
              });
            }}
            label="Audiencia"
          >
            <MenuItem value="manual_selection">Selección manual</MenuItem>
            <MenuItem value="people">Personas</MenuItem>
            <MenuItem value="groups">Grupos/Comunidades</MenuItem>
            <MenuItem value="labels">Etiquetas</MenuItem>
            <MenuItem value="contact_labels">Etiquetas de contactos</MenuItem>
            <MenuItem value="funnel_stage">Embudo y etapa</MenuItem>
            <MenuItem value="saved_list">Lista guardada</MenuItem>
          </Select>
        </FormControl>

        {form.audienceType === "labels" && (
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Etiquetas</InputLabel>
            <Select
              multiple
              value={form.labelIds}
              onChange={event =>
                updateForm({ labelIds: event.target.value.map(Number) })
              }
              renderValue={selected =>
                selected
                  .map(labelId => labels.find(label => label.id === labelId)?.name)
                  .filter(Boolean)
                  .join(", ")
              }
              label="Etiquetas"
            >
              {labels.map(label => (
                <MenuItem key={label.id} value={label.id}>
                  <Checkbox checked={form.labelIds.includes(label.id)} color="primary" />
                  {label.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {form.audienceType === "contact_labels" && (
          <>
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Campo de etiquetas</InputLabel>
              <Select
                value={form.contactLabelField}
                onChange={event =>
                  updateForm({
                    contactLabelField: event.target.value,
                    contactLabelNames: [],
                  })
                }
                label="Campo de etiquetas"
              >
                {contactLabelFilters.map(field => (
                  <MenuItem key={field.name} value={field.name}>
                    {field.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Etiquetas de contactos</InputLabel>
              <Select
                multiple
                disabled={!form.contactLabelField}
                value={form.contactLabelNames}
                onChange={event =>
                  updateForm({ contactLabelNames: event.target.value })
                }
                renderValue={selected => selected.join(", ")}
                label="Etiquetas de contactos"
              >
                {(selectedContactLabelFilter?.labels || []).map(label => (
                  <MenuItem key={`${form.contactLabelField}-${label.name}`} value={label.name}>
                    <Checkbox
                      checked={form.contactLabelNames.includes(label.name)}
                      color="primary"
                    />
                    {label.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {form.audienceType === "funnel_stage" && (
          <>
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Embudo</InputLabel>
              <Select
                value={form.funnelId || NO_CAMPAIGN_FUNNEL}
                onChange={event =>
                  updateForm({
                    funnelId:
                      event.target.value === NO_CAMPAIGN_FUNNEL
                        ? ""
                        : event.target.value,
                    stageId: "",
                  })
                }
                label="Embudo"
              >
                <MenuItem value={NO_CAMPAIGN_FUNNEL}>Seleccionar embudo</MenuItem>
                {funnels.map(funnel => (
                  <MenuItem key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Etapa</InputLabel>
              <Select
                value={form.stageId || NO_CAMPAIGN_STAGE}
                onChange={event =>
                  updateForm({
                    stageId:
                      event.target.value === NO_CAMPAIGN_STAGE ? "" : event.target.value,
                  })
                }
                label="Etapa"
              >
                <MenuItem value={NO_CAMPAIGN_STAGE}>Seleccionar etapa</MenuItem>
                {(selectedFunnel?.stages || []).map(stage => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {form.audienceType === "saved_list" && (
          <div className={classes.selectWithActions}>
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Lista guardada</InputLabel>
              <Select
                value={form.campaignListId || NO_CAMPAIGN_LIST}
                onChange={event => {
                  const value =
                    event.target.value === NO_CAMPAIGN_LIST ? "" : event.target.value;
                  const list = lists.find(item => String(item.id) === String(value));
                  setListEditMode(false);
                  setOriginalListTicketIds([]);
                  setPreviewSearchParam("");
                  setPreviewTickets([]);
                  setSelectedTicketIds([]);
                  updateForm({
                    campaignListId: value,
                    listName: list?.name || "",
                  });
                }}
                label="Lista guardada"
              >
                <MenuItem value={NO_CAMPAIGN_LIST}>Seleccionar lista</MenuItem>
                {lists.map(list => (
                  <MenuItem key={list.id} value={list.id}>
                    {formatListOption(list)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Editar lista">
              <span>
                <IconButton
                  size="small"
                  onClick={openRenameListModal}
                  disabled={!form.campaignListId}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={listEditMode ? "Salir de edición" : "Editar tickets de la lista"}>
              <span>
                <IconButton
                  size="small"
                  onClick={toggleListEditMode}
                  disabled={!form.campaignListId}
                >
                  {listEditMode ? <Remove fontSize="small" /> : <PlaylistAddCheck fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Eliminar lista">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setDeletingList(selectedList)}
                  disabled={!form.campaignListId}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </div>

      <div className={classes.sectionHeader}>
        <Typography variant="subtitle2">
          Tickets seleccionados: {selectedTicketIds.length}
        </Typography>
        <Button size="small" color="primary" onClick={toggleAllPreviewTickets}>
          Seleccionar vista
        </Button>
      </div>

      <TextField
        placeholder="Buscar por nombre o número"
        type="search"
        value={previewSearchParam}
        onChange={event => setPreviewSearchParam(event.target.value)}
        variant="outlined"
        margin="dense"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "gray" }} />
            </InputAdornment>
          ),
        }}
      />

      {listEditMode && (
        <div className={classes.listEditBanner}>
          <div>
            <Typography variant="subtitle2">
              Editando tickets de la lista {formatListOption(selectedList) || form.listName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Agrega o quita tickets marcando las tarjetas visibles.
            </Typography>
          </div>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setListUpdateConfirmation(true)}
            disabled={!listSelectionChanged || !selectedTicketIds.length}
          >
            Actualizar lista
          </Button>
        </div>
      )}

      {form.audienceType === "manual_selection" && (
        <div className={classes.listActions}>
          <TextField
            label="Nombre de la lista"
            value={form.listName}
            onChange={event => updateForm({ listName: event.target.value })}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <Button
            color="primary"
            variant="outlined"
            onClick={saveCampaignList}
            disabled={!selectedTicketIds.length}
          >
            Guardar lista
          </Button>
        </div>
      )}

      <div className={classes.ticketGridWrapper}>
        <div className={classes.ticketGrid}>
          {filteredPreviewTickets.map(renderTicketCard)}
        </div>
        {!previewLoading && !filteredPreviewTickets.length && (
          <div className={classes.emptyPreview}>
            <Typography variant="body2">Sin tickets para mostrar.</Typography>
          </div>
        )}
        {previewLoading && (
          <div className={classes.previewLoadingOverlay}>
            <CircularProgress size={18} />
          </div>
        )}
      </div>
      <TablePagination
        component="div"
        count={previewTicketCount}
        page={previewPageNumber - 1}
        rowsPerPage={previewRowsPerPage}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Contactos por pagina"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
        }
        onChangePage={handlePreviewPageChange}
        onChangeRowsPerPage={handlePreviewRowsPerPageChange}
      />
    </>
  );

  const renderMessageControls = () => (
    <>
      <FormControl variant="outlined" margin="dense" fullWidth>
        <InputLabel>Accion</InputLabel>
        <Select
          value={form.actionType}
          onChange={event => updateForm({ actionType: event.target.value })}
          label="Accion"
        >
          <MenuItem value="message">Mensaje</MenuItem>
          <MenuItem value="sequence">Secuencia</MenuItem>
        </Select>
      </FormControl>

      {form.actionType === "sequence" ? (
        <>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Secuencia</InputLabel>
            <Select
              value={form.chatbotSequenceId || NO_SEQUENCE}
              onChange={event => {
                if (event.target.value === "__create__") {
                  openCreateSequence();
                  return;
                }
                updateForm({
                  chatbotSequenceId:
                    event.target.value === NO_SEQUENCE ? "" : event.target.value,
                });
              }}
              label="Secuencia"
            >
              <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
              <MenuItem value="__create__">Crear secuencia...</MenuItem>
              {sequences.map(sequence => (
                <MenuItem key={sequence.id} value={String(sequence.id)}>
                  {formatSequenceOption(sequence)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div>
            <Button
              color="primary"
              disabled={!form.chatbotSequenceId}
              onClick={() => openEditSequence(form.chatbotSequenceId)}
            >
              Editar secuencia
            </Button>
          </div>
        </>
      ) : (
        <>
          {blocks.map((block, index) => (
            <Paper className={classes.block} elevation={0} key={`campaign-block-${index}`}>
              <div className={classes.blockHeader}>
                <Typography variant="subtitle2">Mensaje {index + 1}</Typography>
                <div className={classes.blockActions}>
                  <IconButton size="small" onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}>
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => removeBlock(index)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </div>
              </div>

              <div className={classes.messageBlockGrid}>
                <MessageBubblePreview block={block} />
                <TextField
                  label="Mensaje"
                  value={block.text}
                  onChange={event => updateBlock(index, { text: event.target.value })}
                  variant="outlined"
                  margin="dense"
                  multiline
                  minRows={5}
                  fullWidth
                  placeholder="Escribe el mensaje..."
                />
                <TextField
                  className={classes.messageDelayField}
                  label="Espera de"
                  type="number"
                  value={block.delaySeconds}
                  onChange={event =>
                    updateBlock(index, {
                      delaySeconds: Math.max(0, Number(event.target.value)),
                    })
                  }
                  variant="outlined"
                  margin="dense"
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Segundos"
                  fullWidth
                />
              </div>

              <div className={classes.variablesBox}>
                <Typography variant="caption" className={classes.variablesLabel}>
                  Variables disponibles
                </Typography>
                <div className={classes.variablesChips}>
                  {messageVariables.map(variable => (
                    <Chip
                      key={`campaign-${index}-${variable.value}`}
                      size="small"
                      label={variable.label}
                      onClick={() => addVariableToBlock(index, variable.value)}
                    />
                  ))}
                </div>
              </div>

              {selectedHasGroup && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={Boolean(block.mentionAll)}
                      onChange={event =>
                        updateBlock(index, { mentionAll: event.target.checked })
                      }
                    />
                  }
                  label="Mencionar a todos (Solo aplica en grupos)"
                />
              )}

              <div className={classes.fileRow}>
                <input
                  accept={acceptedMedia}
                  id={`campaign-file-${index}`}
                  type="file"
                  style={{ display: "none" }}
                  onChange={event =>
                    updateBlock(index, {
                      file: event.target.files?.[0] || null,
                      media: event.target.files?.[0] ? null : block.media,
                    })
                  }
                />
                <label htmlFor={`campaign-file-${index}`}>
                  <Button variant="outlined" component="span" size="small">
                    Seleccionar archivo
                  </Button>
                </label>
                <Typography variant="body2" className={classes.fileName}>
                  {block.file?.name || block.media?.originalName || "Sin archivo"}
                </Typography>
                {(block.file || block.media) && (
                  <Button size="small" onClick={() => updateBlock(index, { file: null, media: null })}>
                    Quitar
                  </Button>
                )}
              </div>
              <small className={classes.helper}>
                Imagenes, stickers .webp, audios, videos y documentos.
              </small>
            </Paper>
          ))}

          <Button
            startIcon={<Add />}
            color="primary"
            onClick={() => setBlocks(prevBlocks => [...prevBlocks, createBlock()])}
            style={{ marginTop: 12 }}
          >
            Agregar mensaje
          </Button>
        </>
      )}
    </>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCampaign
            ? `Eliminar campaña ${deletingCampaign.name}?`
            : "Eliminar campaña"
        }
        open={Boolean(deletingCampaign)}
        onClose={() => setDeletingCampaign(null)}
        onConfirm={deleteCampaign}
      >
        Esta acción no se puede deshacer.
      </ConfirmationModal>

      <ConfirmationModal
        title={deletingList ? `Eliminar lista ${formatListOption(deletingList)}?` : "Eliminar lista"}
        open={Boolean(deletingList)}
        onClose={() => setDeletingList(null)}
        onConfirm={deleteCampaignList}
      >
        Esta acción eliminará la lista guardada, pero no eliminará los tickets.
      </ConfirmationModal>

      <Dialog
        open={Boolean(renamingList)}
        onClose={() => {
          setRenamingList(null);
          setRenamingListName("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar nombre de lista</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre de la lista"
            value={renamingListName}
            onChange={event => setRenamingListName(event.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenamingList(null);
              setRenamingListName("");
            }}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={renameCampaignList}
            color="primary"
            variant="contained"
            disabled={!renamingListName.trim() || renamingListName.trim() === renamingList?.name}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={listUpdateConfirmation}
        onClose={() => setListUpdateConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Actualizar lista</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            ¿Deseas aplicar estos cambios a la lista {formatListOption(selectedList) || form.listName}?
          </Typography>
          <Typography variant="subtitle2" style={{ marginTop: 12 }}>
            Se agregarán
          </Typography>
          {listAddedTicketIds.length ? (
            listAddedTicketIds.map(ticketId => (
              <Typography key={`add-${ticketId}`} variant="body2" color="textSecondary">
                + {getTicketLabel(ticketId)}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">Sin agregados.</Typography>
          )}
          <Typography variant="subtitle2" style={{ marginTop: 12 }}>
            Se quitarán
          </Typography>
          {listRemovedTicketIds.length ? (
            listRemovedTicketIds.map(ticketId => (
              <Typography key={`remove-${ticketId}`} variant="body2" color="textSecondary">
                - {getTicketLabel(ticketId)}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">Sin eliminados.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListUpdateConfirmation(false)} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={updateCampaignListTickets} color="primary" variant="contained">
            Aplicar cambios
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(startConfirmation)}
        onClose={closeStartConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Iniciar campaña</DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.startWarningText}>
            {campaignStartWarning}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={startTermsAccepted}
                onChange={event => setStartTermsAccepted(event.target.checked)}
              />
            }
            label="Entiendo y acepto los términos de uso para iniciar esta campaña."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStartConfirmation} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={confirmStartCampaign}
            color="primary"
            variant="contained"
            disabled={!startTermsAccepted || saving}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(progressCampaign)}
        onClose={closeProgressModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          Progreso de envío{progressCampaign ? ` - ${progressCampaign.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {progressCampaign && (
            <>
              <div className={classes.progressSummary}>
                <div className={classes.progressMetric}>
                  <Typography variant="subtitle2">{progressCampaign.totalRecipients || 0}</Typography>
                  <Typography variant="caption" color="textSecondary">Total</Typography>
                </div>
                <div className={classes.progressMetric}>
                  <Typography variant="subtitle2">{progressCampaign.sentCount || 0}</Typography>
                  <Typography variant="caption" color="textSecondary">Enviados</Typography>
                </div>
                <div className={classes.progressMetric}>
                  <Typography variant="subtitle2">{progressCampaign.pendingCount || 0}</Typography>
                  <Typography variant="caption" color="textSecondary">Pendientes</Typography>
                </div>
                <div className={classes.progressMetric}>
                  <Typography variant="subtitle2">{progressCampaign.failedCount || 0}</Typography>
                  <Typography variant="caption" color="textSecondary">Fallidos</Typography>
                </div>
              </div>

              <div className={classes.progressToolbar}>
                <div>
                  {renderStatus(progressCampaign.status)}
                  <Typography variant="body2" color="textSecondary">
                    Siguiente envío:{" "}
                    {progressCampaign.status === "running"
                      ? getRemainingTime(
                          progressRecipients.find(
                            recipient => recipient.status === "pending" && recipient.nextRunAt
                          )?.nextRunAt
                        )
                      : "-"}
                  </Typography>
                </div>
                <div className={classes.actionButtons}>
                  {["draft", "scheduled", "paused", "cancelled", "completed", "failed"].includes(progressCampaign.status) && (
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      disabled={Boolean(runningCampaign && runningCampaign.id !== progressCampaign.id)}
                      onClick={() => requestStartCampaign(progressCampaign)}
                    >
                      Continuar
                    </Button>
                  )}
                  {progressCampaign.status === "running" && (
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<Pause />}
                      onClick={() => updateCampaignStatus(progressCampaign, "pause")}
                    >
                      Pausar
                    </Button>
                  )}
                  {["running", "scheduled", "paused"].includes(progressCampaign.status) && (
                    <Button
                      size="small"
                      color="secondary"
                      variant="outlined"
                      startIcon={<Stop />}
                      onClick={() => updateCampaignStatus(progressCampaign, "cancel")}
                    >
                      Detener
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    startIcon={<GetApp />}
                    onClick={downloadProgressReport}
                  >
                    Reporte TXT
                  </Button>
                </div>
              </div>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ticket</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Fecha de envío</TableCell>
                    <TableCell align="center">Tiempo restante</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {progressRecipients.map(renderProgressRecipient)}
                  {progressLoading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProgressModal} color="secondary" variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalOpen} onClose={closeModal} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          {editingCampaign ? "Editar campaña" : "Agregar campaña"}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.formGrid}>
            <TextField
              className={classes.formGridFull}
              label="Nombre"
              value={form.name}
              onChange={event => updateForm({ name: event.target.value })}
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
            />
            <TextField
              label="Intervalo minimo"
              type="number"
              value={form.minDelaySeconds}
              onChange={event =>
                updateForm({ minDelaySeconds: Math.max(0, Number(event.target.value)) })
              }
              variant="outlined"
              margin="dense"
              helperText="Segundos"
            />
            <TextField
              label="Intervalo maximo"
              type="number"
              value={form.maxDelaySeconds}
              onChange={event =>
                updateForm({ maxDelaySeconds: Math.max(0, Number(event.target.value)) })
              }
              variant="outlined"
              margin="dense"
              helperText="Segundos"
            />
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Ejecucion</InputLabel>
              <Select
                value={form.scheduleMode}
                onChange={event =>
                  updateForm({
                    scheduleMode: event.target.value,
                    scheduledAt:
                      event.target.value === "scheduled" ? form.scheduledAt : "",
                  })
                }
                label="Ejecucion"
              >
                <MenuItem value="immediate">Inmediata</MenuItem>
                <MenuItem value="scheduled">Programada</MenuItem>
              </Select>
            </FormControl>
            {form.scheduleMode === "scheduled" && (
              <TextField
                label="Fecha y hora"
                type="datetime-local"
                value={form.scheduledAt}
                onChange={event => updateForm({ scheduledAt: event.target.value })}
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          </div>

          <div className={classes.section}>
            <Typography variant="subtitle2">Audiencia</Typography>
            {renderAudienceControls()}
          </div>

          <div className={classes.section}>
            <Typography variant="subtitle2">Contenido</Typography>
            {renderMessageControls()}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="secondary" variant="outlined" disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => saveCampaign("draft")} color="primary" variant="outlined" disabled={saving}>
            Guardar borrador
          </Button>
          <Button onClick={requestSaveAndStart} color="primary" variant="contained" disabled={saving}>
            {form.scheduleMode === "scheduled" ? "Guardar programación" : "Guardar e iniciar"}
            {saving && <CircularProgress size={20} style={{ marginLeft: 8 }} />}
          </Button>
        </DialogActions>
      </Dialog>

      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={selectedConnectionId}
        initialSequenceName="Secuencia de campana"
      />

      <MainHeader>
        <div className={classes.headerContent}>
          <Title>Campañas</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <TextField
              placeholder="Buscar ..."
              type="search"
              value={searchParam}
              onChange={event => setSearchParam(event.target.value.toLowerCase())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
              onClick={openNewCampaign}
            >
              Agregar campaña
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexion ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>

        <Typography variant="subtitle2" style={{ marginTop: 12, marginBottom: 8 }}>
          Campañas inmediatas
        </Typography>

        <div className={classes.mobileCards}>
          {immediateCampaigns.map(renderMobileCampaign)}
          {loading && <CircularProgress size={24} />}
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell align="center">Conexion</TableCell>
                <TableCell align="center">Audiencia</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Progreso</TableCell>
                <TableCell align="center">Contenido</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {immediateCampaigns.map(renderCampaignRow)}
              {loading && <TableRowSkeleton columns={7} />}
            </TableBody>
          </Table>
        </div>

        <Typography variant="subtitle2" style={{ marginTop: 24, marginBottom: 8 }}>
          Campañas programadas
        </Typography>

        <div className={classes.mobileCards}>
          {scheduledCampaigns.map(renderMobileCampaign)}
          {!loading && !scheduledCampaigns.length && (
            <Typography variant="body2" color="textSecondary">
              Sin campañas programadas.
            </Typography>
          )}
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell align="center">Conexion</TableCell>
                <TableCell align="center">Audiencia</TableCell>
                <TableCell align="center">Fecha programada</TableCell>
                <TableCell align="center">Progreso</TableCell>
                <TableCell align="center">Contenido</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledCampaigns.map(renderScheduledCampaignRow)}
              {!loading && !scheduledCampaigns.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Sin campañas programadas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Campaigns;
